import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import WorkspaceSwitcher from '../WorkspaceSwitcher';

const Sidebar = ({ activeTab, onTabChange, user, onLogout, onCreateWorkspace }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isCollapsed]);

  const menuItems = [
    {
      id: 'prompts',
      label: 'Prompt Library',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
        </svg>
      )
    },
    {
      id: 'refine',
      label: 'Refine',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9"></path>
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
        </svg>
      )
    },
    {
      id: 'test',
      label: 'Test & Preview',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
      )
    },
    {
      id: 'compare',
      label: 'Compare Models',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="18" rx="1" />
          <rect x="14" y="3" width="7" height="18" rx="1" />
          <path d="M12 8V16" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 2" />
        </svg>
      )
    },
    {
      id: 'monitoring',
      label: 'Analytics',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'versions',
      label: 'Version Control',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/>
        </svg>
      )
    },
    {
      id: 'sdk',
      label: 'SDK & API Access',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
        </svg>
      )
    }
  ];

  return (
    <aside className={`
      relative
      ${isCollapsed ? 'w-[72px] min-w-[72px]' : 'w-[280px] min-w-[280px]'}
      h-full flex flex-col flex-shrink-0
      transition-all duration-300 ease-in-out
      bg-gradient-to-b from-bg-secondary to-bg-primary border-r border-primary-500/10
    `}>
      {/* Logo & Toggle Section */}
      <div className={`flex-shrink-0 ${isCollapsed ? 'py-4' : 'py-6 px-5'} border-b border-primary-500/10`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between gap-3 pb-4'} ${isCollapsed ? '' : 'border-b border-primary-500/[0.08]'}`}>
          {!isCollapsed && (
            <>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <img 
                  src="/logo.png"
                  alt="ForgeVault Logo" 
                  className="w-9 h-9 flex-shrink-0 object-contain"
                />
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="text-base font-semibold leading-tight tracking-tight truncate text-text-primary">ForgeVault</span>
                  <span className="text-xs leading-tight font-normal truncate text-primary-500">Where Prompts Evolve</span>
                </div>
              </div>
            </>
          )}
          
          {isCollapsed && (
            <img 
              src="/logo.png"
              alt="ForgeVault Logo" 
              className="w-9 h-9 flex-shrink-0 object-contain"
            />
          )}
        </div>
        
        {/* Workspace Switcher or Icon */}
        {!isCollapsed && (
          <div className="w-full pt-4">
            <WorkspaceSwitcher onCreateNew={onCreateWorkspace} />
          </div>
        )}
        
        {isCollapsed && (
          <div className="w-full flex justify-center pt-4">
            <button
              onClick={onCreateWorkspace}
              className="w-10 h-10 bg-bg-tertiary border border-border-primary rounded-lg flex items-center justify-center hover:bg-bg-hover hover:border-primary-500 transition-all duration-200 group"
              title="Workspaces"
            >
              <svg className="w-5 h-5 text-text-secondary group-hover:text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </button>
          </div>
        )}
        
        {/* Toggle Button - Styled to match app theme */}
        <div className={`${isCollapsed ? 'flex justify-center pt-4' : 'absolute -right-3 top-8'}`}>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-6 h-6 bg-bg-secondary border border-primary-500 rounded-md flex items-center justify-center shadow-sm
                       hover:bg-bg-hover hover:border-primary-600 hover:shadow-md
                       transition-all duration-200 group z-10"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg 
              className={`w-3.5 h-3.5 text-text-tertiary group-hover:text-primary-500 transition-all duration-200 ${
                isCollapsed ? 'rotate-0' : 'rotate-180'
              }`} 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5"
            >
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Menu Section */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 min-h-0">
        <nav className={`flex flex-col gap-1 ${isCollapsed ? 'px-2' : 'px-3'}`}>
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`
                flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-lg cursor-pointer 
                transition-all duration-200 text-left w-full relative select-none group
                ${isCollapsed ? 'justify-center' : ''}
                ${activeTab === item.id 
                  ? 'bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm font-semibold' 
                  : 'bg-transparent text-text-secondary hover:bg-primary-500/10 hover:text-text-primary'
                }
              `}
              onClick={() => onTabChange(item.id)}
              title={isCollapsed ? item.label : ''}
            >
              <span className={activeTab === item.id ? 'text-black' : 'text-text-tertiary group-hover:text-primary-400'}>
                {item.icon}
              </span>
              {!isCollapsed && (
                <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>
              )}
            </button>
          ))}
          
          <div className={`mt-4 pt-4 ${isCollapsed ? 'mx-1' : ''} border-t border-primary-500/10`}>
            <button
              className={`
                flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-lg cursor-pointer 
                transition-all duration-200 text-left w-full relative select-none group
                ${isCollapsed ? 'justify-center' : ''}
                ${activeTab === 'workspace-settings' 
                  ? 'bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm font-semibold' 
                  : 'bg-transparent text-text-secondary hover:bg-primary-500/10 hover:text-text-primary'
                }
              `}
              onClick={() => onTabChange('workspace-settings')}
              title={isCollapsed ? 'Workspace' : ''}
            >
              <svg className={`w-5 h-5 flex-shrink-0 ${activeTab === 'workspace-settings' ? 'text-black' : 'text-text-tertiary'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              {!isCollapsed && (
                <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">Workspace</span>
              )}
            </button>
          </div>
        </nav>
      </div>

      {/* Account Section */}
      {user && (
        <div className="py-4 flex-shrink-0 border-t border-primary-500/10">
          {!isCollapsed ? (
            <>
              <button 
                onClick={() => onTabChange('profile-settings')}
                className="flex items-center gap-3 py-2 px-5 mb-2 w-full text-left rounded-lg transition-colors bg-transparent hover:bg-primary-500/5"
                title="Profile Settings"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold bg-gradient-to-br from-primary-600 to-primary-500 shadow-glow-sm text-black">
                  {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis text-text-primary">{user.username || 'User'}</span>
                </div>
              </button>

              <div className="px-3">
                <button 
                  className="flex items-center gap-3 py-2 px-3 text-sm font-medium rounded-lg cursor-pointer transition-all duration-200 text-left w-full relative select-none text-error bg-transparent hover:bg-error/10"
                  onClick={onLogout}
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">Sign Out</span>
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 px-2">
              <button
                className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 text-base font-semibold bg-gradient-to-br from-primary-600 to-primary-500 shadow-glow-sm text-black"
                onClick={() => onTabChange('profile-settings')}
                title="Profile Settings"
              >
                {(user.username || user.email || 'U').charAt(0).toUpperCase()}
              </button>
              
              <button 
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 bg-error/10 text-error hover:bg-error hover:text-white"
                onClick={onLogout}
                title="Sign Out"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

    </aside>
  );
};

Sidebar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  user: PropTypes.shape({
    user_id: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
  }),
  onLogout: PropTypes.func,
  onCreateWorkspace: PropTypes.func,
};

Sidebar.defaultProps = {
  user: null,
  onLogout: () => {},
  onCreateWorkspace: () => {},
};

export default Sidebar;
